import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { getUsageStats, FREE_TIER_LIMITS, FEATURE_NAMES, type UsageFeature, type MonthlyUsage, type SubscriptionTier } from "../lib/userStats";

interface UsageIndicatorProps {
  feature: UsageFeature;
  compact?: boolean;
  onLimitReached?: () => void;
  refreshKey?: number; // Increment to trigger refresh
}

export default function UsageIndicator({ feature, compact = false, onLimitReached, refreshKey = 0 }: UsageIndicatorProps) {
  const { user } = useAuth();
  const [usage, setUsage] = useState<(MonthlyUsage & { tier: SubscriptionTier }) | null>(null);

  useEffect(() => {
    if (user) {
      getUsageStats(user.uid).then(setUsage).catch(console.error);
    }
  }, [user, refreshKey]);

  if (!usage) return null;

  // Premium users don't see limits
  if (usage.tier === "premium") {
    if (compact) return null;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium font-[family-name:--font-ui] bg-accent/10 text-accent">
        Premium
      </span>
    );
  }

  const limit = FREE_TIER_LIMITS[feature];
  const current = usage[feature] || 0;
  const remaining = Math.max(0, limit - current);
  const percentage = limit > 0 ? (current / limit) * 100 : 100;

  // Premium-only feature
  if (limit === 0) {
    return (
      <button
        onClick={onLimitReached}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium font-[family-name:--font-ui] bg-accent/10 text-accent hover:bg-accent/20 transition-colors cursor-pointer"
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Premium
      </button>
    );
  }

  if (compact) {
    // Just show remaining count
    const isLow = remaining <= 2;
    const isEmpty = remaining === 0;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium font-[family-name:--font-ui] ${
          isEmpty
            ? "bg-gi-high-bg text-gi-high"
            : isLow
              ? "bg-gi-medium-bg text-gi-medium"
              : "text-muted"
        }`}
      >
        {isEmpty ? (
          <>
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Limit reached
          </>
        ) : (
          `${remaining} left`
        )}
      </span>
    );
  }

  // Full display with progress bar
  const featureName = FEATURE_NAMES[feature];

  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-text font-[family-name:--font-ui] font-medium">{featureName}</span>
        <span className="text-xs text-muted font-[family-name:--font-ui]">
          {current} / {limit} used
        </span>
      </div>
      <div className="h-1.5 bg-border rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            percentage >= 100
              ? "bg-gi-high"
              : percentage >= 80
                ? "bg-gi-medium"
                : "bg-accent"
          }`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>
      {remaining <= 2 && remaining > 0 && (
        <p className="text-xs text-gi-medium mt-2 font-[family-name:--font-ui]">
          Only {remaining} {remaining === 1 ? "use" : "uses"} remaining this month
        </p>
      )}
      {remaining === 0 && (
        <p className="text-xs text-gi-high mt-2 font-[family-name:--font-ui]">
          Limit reached. Upgrade for unlimited access.
        </p>
      )}
    </div>
  );
}

// Dashboard component showing all feature usage
export function UsageDashboard() {
  const { user } = useAuth();
  const [usage, setUsage] = useState<(MonthlyUsage & { tier: SubscriptionTier }) | null>(null);

  useEffect(() => {
    if (user) {
      getUsageStats(user.uid).then(setUsage).catch(console.error);
    }
  }, [user]);

  if (!usage) return null;

  if (usage.tier === "premium") {
    return (
      <div className="py-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-accent font-[family-name:--font-ui] font-medium">Premium Member</span>
        </div>
        <p className="text-xs text-muted font-[family-name:--font-ui]">Unlimited access to all features</p>
      </div>
    );
  }

  const features: UsageFeature[] = ["chat", "mealBuilder", "recipeTransformer", "menuDecoder"];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="section-label">Monthly Usage</div>
        <span className="text-xs text-muted font-[family-name:--font-ui]">Resets monthly</span>
      </div>
      <div className="space-y-2">
        {features.map((feature) => {
          const limit = FREE_TIER_LIMITS[feature];
          if (limit === 0) return null; // Skip premium-only features

          const current = usage[feature] || 0;
          const percentage = (current / limit) * 100;

          return (
            <div key={feature} className="flex items-center gap-3">
              <span className="text-xs text-muted font-[family-name:--font-ui] w-24 truncate">{FEATURE_NAMES[feature]}</span>
              <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    percentage >= 100
                      ? "bg-gi-high"
                      : percentage >= 80
                        ? "bg-gi-medium"
                        : "bg-accent"
                  }`}
                  style={{ width: `${Math.min(100, percentage)}%` }}
                />
              </div>
              <span className="text-xs text-muted font-[family-name:--font-ui] w-12 text-right">
                {current}/{limit}
              </span>
            </div>
          );
        })}
      </div>
      <button className="w-full mt-2 py-2 rounded-md bg-accent text-white text-sm font-[family-name:--font-ui] font-medium hover:opacity-85 transition-opacity">
        Upgrade for Unlimited
      </button>
    </div>
  );
}
